'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { ubuntu, noto_sans } from '@/globalConstants';
import { useAppContext } from '@/contexts/AppContext';
import { event } from '@/services/gtag';

const DISMISS_KEY = 'dharmayana_app_nudge_dismissed_at';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const ANDROID_STORE =
  process.env.NEXT_PUBLIC_KOCHAVA_LINK_ANDROID ||
  'https://play.google.com/store/apps/details?id=in.dharmayana.android&pcampaignid=web_share';
const IOS_STORE =
  process.env.NEXT_PUBLIC_KOCHAVA_LINK_IOS ||
  'https://apps.apple.com/in/app/dharmayana-hindu-app/id6474740467';

const AppInstallNudge = () => {
  const { isMobileView, isAppView } = useAppContext();
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [platform, setPlatform] = useState<'android' | 'ios'>('android');

  useEffect(() => {
    if (isAppView || !isMobileView) return;

    // Respect prior dismissal (7-day TTL).
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION_MS) return;
    } catch {
      // localStorage may be unavailable (private mode) — just proceed.
    }

    const ua = navigator.userAgent || '';
    setPlatform(/iPhone|iPad|iPod/i.test(ua) ? 'ios' : 'android');

    setVisible(true);
    // Trigger slide-up after mount so the CSS transition plays.
    const t = setTimeout(() => setEntered(true), 50);
    return () => clearTimeout(t);
  }, [isMobileView, isAppView]);

  const storeUrl = platform === 'ios' ? IOS_STORE : ANDROID_STORE;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setEntered(false);
    setTimeout(() => setVisible(false), 250);
  };

  const handleInstallClick = () => {
    event({
      action: platform === 'ios' ? 'Nudge_IOS_Install_Clicked' : 'Nudge_Android_Install_Clicked',
      category: 'app_install_nudge',
      label: 'sticky_bottom_nudge',
      value: 'install',
    });
  };

  if (!visible) return null;

  return (
    <>
      {/* Spacer so the fixed nudge never covers page content. */}
      <div aria-hidden className="h-[200px] lg:hidden" />

      <div
        role="dialog"
        aria-label="Install the Dharmayana app"
        className={`fixed bottom-0 left-0 right-0 z-[9999] lg:hidden transition-transform duration-300 ease-out ${
          entered ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white rounded-t-[18px] shadow-[0_-6px_24px_rgba(0,0,0,0.18)] overflow-hidden">
          {/* Close row */}
          <div className="flex justify-end px-[14px] pt-[12px]">
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss app install banner"
              className="p-[4px] text-[#2C2C2C]"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Row 1: icon + app name + primary CTA */}
          <div className="flex items-center gap-[12px] px-[16px] pt-[4px] pb-[14px]">
            <div className="shrink-0 w-[56px] h-[56px] rounded-[12px] overflow-hidden bg-[#05574E] flex items-center justify-center">
              <Image
                src="/app-nudge-logo.webp"
                alt="Dharmayana app icon"
                width={60}
                height={60}
                className="object-contain w-[60px] h-[60px]"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className={`${ubuntu.className} font-[700] text-[18px] text-[#111111] leading-tight`}>
                Dharmayana App
              </h3>
            </div>

            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleInstallClick}
              className={`${ubuntu.className} shrink-0 bg-[#1D4749] text-white text-[15px] font-[600] px-[22px] py-[12px] rounded-[10px] whitespace-nowrap active:scale-[0.97] transition`}
            >
              Get App
            </a>
          </div>

          {/* Row 2: stats (three centered columns, no dividers) */}
          <div className={`${noto_sans.className} grid grid-cols-3 px-[16px] pb-[16px]`}>
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-[4px] text-[15px] font-[500] text-[#111111]">
                4.6
                <Image src="/Star.svg" alt="" width={14} height={14} className="w-[14px] h-[14px]" />
              </div>
              <div className="text-[12px] text-[#5C5A58] leading-tight mt-[2px]">5K+ reviews</div>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="text-[15px] font-[500] text-[#111111]">500K+</div>
              <div className="text-[12px] text-[#5C5A58] leading-tight mt-[2px]">Downloads</div>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center justify-center text-[12px] font-[500] text-[#111111] border border-[#111111] rounded-[3px] px-[6px] leading-[1.4] min-w-[26px]">
                3+
              </div>
              <div className="text-[12px] text-[#5C5A58] leading-tight mt-[2px]">Rated for 3+</div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-[1px] bg-[#E5E0DF] mx-[16px]" />

          {/* Row 3: continue in browser */}
          <div className="flex items-center gap-[12px] px-[16px] py-[14px] pb-[18px]">
            <div className="shrink-0 w-[40px] h-[40px] flex items-center justify-center">
              {/* Simple browser/globe icon */}
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#5C5A58" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <p className={`${ubuntu.className} font-[600] text-[16px] text-[#111111] leading-tight`}>
                In browser
              </p>
            </div>

            <button
              type="button"
              onClick={dismiss}
              className={`${ubuntu.className} shrink-0 bg-[#1F1F1F] text-white text-[15px] font-[600] px-[22px] py-[12px] rounded-[10px] whitespace-nowrap active:scale-[0.97] transition`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppInstallNudge;
